import apiClient from './api';

interface ReviewSubmission {
  deliveryId: string;
  rating: number;
  comment?: string;
  images?: File[];
  existingImages?: string[];
}

interface Review {
  id: string;
  deliveryId: string;
  userId: string;
  rating: number;
  comment?: string;
  images?: string[];
  createdAt: string;
}

const reviewService = {
  // Submit a new review
  async submitReview(data: ReviewSubmission): Promise<Review> {
    const formData = new FormData();
    formData.append('deliveryId', data.deliveryId);
    formData.append('rating', data.rating.toString());
    
    if (data.comment) {
      formData.append('comment', data.comment);
    }
    
    if (data.images && data.images.length > 0) {
      data.images.forEach((image) => {
        formData.append('images', image);
      });
    }
    
    if (data.existingImages && data.existingImages.length > 0) {
      formData.append('existingImages', JSON.stringify(data.existingImages));
    }
    
    try {
      // First try with /api prefix
      const response = await apiClient.post('/reviews', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.data;
    } catch (error: any) {
      // Check if we got a 404 (Not Found)
      if (error.response && error.response.status === 404) {
        // Try without /api prefix as fallback
        const fallbackResponse = await apiClient.post('/reviews', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return fallbackResponse.data.data;
      }
      throw error;
    }
  },
  
  // Get review for a specific delivery
  async getReviewByDeliveryId(deliveryId: string): Promise<Review | null> {
    try {
      // First try with /api prefix
      try {
        const response = await apiClient.get(`/reviews/delivery/${deliveryId}`);
        return response.data.data;
      } catch (prefixError: any) {
        // If we get a 404, try without the prefix
        if (prefixError.response && prefixError.response.status === 404) {
          const fallbackResponse = await apiClient.get(`/reviews/delivery/${deliveryId}`);
          return fallbackResponse.data.data;
        }
        throw prefixError;
      }
    } catch (error) {
      console.error('Error fetching review:', error);
      // Return null if no review exists
      return null;
    }
  },
  
  // Check if a delivery has been reviewed
  async hasReviewed(deliveryId: string): Promise<boolean> {
    try {
      // First try with /api prefix
      try {
        const response = await apiClient.get(`/reviews/delivery/${deliveryId}/status`);
        return response.data?.data?.hasReviewed || false;
      } catch (prefixError: any) {
        // If we get a 404, try without the prefix
        if (prefixError.response && prefixError.response.status === 404) {
          const fallbackResponse = await apiClient.get(`/reviews/delivery/${deliveryId}/status`);
          return fallbackResponse.data?.data?.hasReviewed || false;
        }
        throw prefixError;
      }
    } catch (error) {
      console.error('Error checking review status:', error);
      return false;
    }
  }
};

export default reviewService; 